// import { lazyInitialize } from 'core-decorators'
import * as nativeComponentListener from "../../../api/nativeComponentListener"
import * as nativeComponentAPI from '../../../api/nativeComponentAPI'

const [
    _createNativeInstance,
    _fireLoadSuccess,
    _createNativeInstancePromise,
    _loadListenerList_symbol
] = [
    Symbol('_createNativeInstance'),
    Symbol('_fireLoadSuccess'),
    Symbol('_createNativeInstancePromise'),
    Symbol('_loadListenerList_symbol'),
]

function fireEvent (eventName, eventData) {
    // wp:createEvent,initEvent已废弃，可考虑使用 CustomEvent
    var ev = document.createEvent('HTMLEvents')
    ev.initEvent(eventName, false, true)
    ev.param = eventData
    document.dispatchEvent(ev)
}


/**
 * 原生组件基类
 *
 * @Overview(概述)：
 *      所有native提供的可供h5端调用的组件，在h5端都有对应的组件对象与之映射，
 *      且都需要继承该 NativeComponent 类。
 *      NativeComponent类提供了基础的支持确保业务代码在调用前端映射组件的任何方法时，
 *      native端已经创建好了对应的组件供我们使用。
 *
 *
 * @Attention：
 *      --NativeComponent类的所有子类凡是以native开头命名的方法，均会被包装一层promise代理--（废弃），
 *      被添加@nativeComponentAbility注解的子类方法，会被包装一层promise代理
 *      使之被调用时，会等到native端已经创建好具体的组件并返回了组件id时，该方法才被实际调用并返回调用结果
 *
 *
 * @time：2019/12/08
 *
 */
class NativeComponent {

    /**
     * 原生组件基类构造方法
     * @constructor
     * @param action 创建原生组件对应的api
     * @param options {Object} 构造参数
     * @param options.id {Number= | String=} 组件id,非必传，有id参数则不会调用sdk的api去创建native组件
     * @param options.params {Object=} 创建原生组件api所需的参数
     */
    constructor (action, options={}) {
        //console.log('this.id===>',options)

        /**
         * 组件id， 映射原生组件的id
         * @type {(Number | String)}
         */
        this._id = options.id || undefined

        if(!this._id && !action){
            throw new Error('required action')
            return false
        }

        /**
         * 组件所在广告位id
         * @type {(Number | String)}
         */
        this.unitId = undefined

        /**
         * 原生组件加载成功的监听队列
         * @type {Array}
         * @private
         */
        this[_loadListenerList_symbol] = []

        this[_createNativeInstancePromise] =
            this._id ?  new Promise(resolve=>{
                            resolve(this._id)
                        })
                     :  this[_createNativeInstance](action, options.params)
        //this[_createNativeInstancePromise] = this[_createNativeInstance](action, options.params)
        this[_createNativeInstancePromise].then((id)=>{
            console.log('=========>native component create success')
            this._id = id
            //this.unitId = unitId
            this[_fireLoadSuccess]()

            fireEvent('nativeComponentCreated', {
                id,
                instance: this
            })
        }).catch(err=>{
            console.error('promise catch an error:',err)
        })

        /*this[_createNativeInstancePromise] = new Promise(resolve => {
            setTimeout(()=>{
                this.id = 1001
                this.unitId = 2002
                console.log('=========>native component create success')
                this[_fireLoadSuccess]()
                resolve()
            },2e3)
        })*/

        this.onDestroy((data)=>{
            console.log('component onDestroy ', data)
        })
    }

    set id(val){
        if(!this._id && (typeof val ==='number' || typeof val === 'string')){
            this._id = val
        }else {
            console.error('设置组件id失败',val)
        }
    }
    get id(){
        return this._id
    }

    getId(){
        return this._id
    }

    /**
     * 通知native创建对应的原生组件
     * @param action 创建原生组件对应的api
     * @param params 创建原生组件api所需的参数
     * @returns {Promise<any>}
     * @private
     */
    [_createNativeInstance] (action, params){
        // console.log('开始创建', action, params)
        return new Promise((resolve, reject) => {
            action({data:params}).then((res)=> {
                const {code, data={}} = res
                if(code === 0) {
                    const componentData = data
                    if(componentData) {
                        const { id } = componentData
                        console.log('=========>native component create success ==>: ',id, typeof id,res)
                        //this.id = id
                        resolve(id)
                    }else {
                        reject(new Error('创建失败'))
                    }
                }else {
                    reject(new Error('创建失败'))
                }

            })
        })
    }

    [_fireLoadSuccess](){
        this[_loadListenerList_symbol].forEach(fn=>{
            fn()
        })
        this[_loadListenerList_symbol].length = 0
    }

    /**
     * 当native端创建组件成功后触发
     * 可以通过传入回调函数或者返回promise的方式监听创建成功
     * @param listener {Function} 监听方法
     */
    onCreateSuccess(listener){
        if(listener && typeof listener === 'function'){
            if(!this.id){
                this[_loadListenerList_symbol].push(listener)
            }else {
                listener.call(this,this)
            }
        }else {
            console.warn('注册组件创建监听，参数类型错误')
        }
    }

    /**
     * 当native端创建组件成功后触发
     * 可以通过传入回调函数或者返回promise的方式监听创建成功
     * @param listener {Function} 监听方法
     */
    whenCreateSuccess(listener){
        return new Promise(resolve => {
                    if(!this.id){
                        this[_loadListenerList_symbol].push(()=>{resolve()})
                    }else {
                        resolve(this)
                    }
                })
    }

    getInvokeParams(params={}){
        return {
            id: this.id,
            data: {
                ...params
            }
        }
    }

    /*******************************************************sdk方法**************************************************/

    /**
     * 销毁组件
     * @param params
     * @returns {*|Promise<any>}
     */
    destory(params) {
        const _params = this.getInvokeParams(params)
        return nativeComponentAPI.destroyComponent(_params)
    }

    /*******************************************************sdk事件**************************************************/

    /**
     * 销毁组件时触发
     * @param cb | Function
     * @returns {*|Promise<any>}
     */
    onDestroy(cb) {
        'function' === typeof cb &&
        nativeComponentListener.onComponentDestroy(this.getId(), cb)
    }


    /*******************************************************装饰器**************************************************/

    /**
     * 装饰器 ，用于标示组件的实际能力由native提供
     * @param target
     * @param name
     * @param descriptor
     */
    static nativeComponentAbility(target, name, descriptor){
        let targetFunction = descriptor.value
        if('function' === typeof targetFunction){
            descriptor.value = function(...arg){
                const component = this
                return component[_createNativeInstancePromise].then(()=>{
                    return targetFunction.call(component, ...arg)
                })
            }
        } else {
            console.warn('nativeComponentAbility装饰器只能装饰在类的实例方法上')
        }
    }

    /**
     * 装饰器 ，用于native组件与sdk交互请求结果的包装
     * @param filter {Function}
     */
    static responseFilter(filter){
        return function(target, name, descriptor){
            let targetFunction = descriptor.value
            if('function' === typeof targetFunction){
                descriptor.value = function(...arg){
                    const component = this
                    return new Promise((resolve, reject)=>{
                        targetFunction.call(component, ...arg).then((res)=>{
                            const filterRes = filter.call(component, res)
                            resolve(filterRes)
                        }).catch(err=>{
                            reject(err)
                        })
                    })

                }
            } else {
                console.warn('nativeComponentAbility装饰器只能装饰在类的实例方法上')
            }
        }
    }

    /**
     * 装饰器 ，用于native组件与sdk交互请求后，基于请求结果给组件设置对应的属性值
     * @param keyValueMap
     */
    static keyValueFilter(keyValueMap){
        return NativeComponent.responseFilter(function(res){
            const { code } = res
            //console.warn('keyValueFilter装饰器参数：',keyValueMap, res, this)
            if(code === 0){
                /*const keys = Object.keys(keyValueMap)
                keys.forEach(key=>{
                    console.log('keyValueFilter', key , keyValueMap, this)
                    this[key] = keyValueMap[key]
                })*/
                const { key, value } = keyValueMap
                if(key && value){
                    this[key] = value
                }else{
                    console.warn('keyValueFilter装饰器参数不正确')
                }

            }
            return res
        })
    }
}

export default NativeComponent